import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaHome,
  FaBriefcase,
  FaHospital,
  FaUserCheck,
  FaHeart,
  FaEnvelope,
  FaUser,
  FaUserCircle,
  FaSignOutAlt,
} from "react-icons/fa";
import hireCuraIcon from "../assets/hirecura-icon.png";

const navLinks = [
  { label: "Home", path: "/", icon: FaHome },
  { label: "Find Jobs", path: "/find-jobs", icon: FaBriefcase },
  { label: "For Employers", path: "/for-employers", icon: FaHospital },
  { label: "How It Works", path: "/how-it-works", icon: FaUserCheck },
  { label: "About Us", path: "/about-us", icon: FaHeart },
  { label: "Contact Us", path: "/contact-us", icon: FaEnvelope },
];

export default function MobileMenu({ onLogin, loggedUser, onLogout }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visibleLinks = navLinks.filter(
    (item) => item.label !== "For Employers" || loggedUser?.role !== "candidate"
  );

  return (
    <>
      {/* Match the Homepage hamburger button sizing */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={open}
        className="lg:hidden ml-auto p-2 rounded-lg text-2xl text-teal-700 transition hover:bg-teal-50"
      >
        <FaBars />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 lg:hidden"
          onClick={() => setOpen(false)}
        >
          <aside
            className="absolute right-0 top-0 box-border flex h-[100dvh] w-[85vw] max-w-[26rem] flex-col overflow-x-hidden overflow-y-auto overscroll-contain bg-gradient-to-b from-teal-700 to-emerald-800 p-5 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            aria-label="Mobile navigation menu"
          >
            {/* Same drawer header as Homepage */}
            <div className="mb-6 flex min-w-0 items-center justify-between gap-3 border-b border-white/20 pb-5">
              <button
                type="button"
                onClick={() => goTo("/")}
                className="flex min-w-0 items-center gap-3"
              >
                <img
                  src={hireCuraIcon}
                  alt="HireCura"
                  className="h-10 w-10 shrink-0 rounded-lg bg-white/10 object-contain p-1"
                />
                <span className="truncate text-2xl font-bold text-white">HireCura</span>
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="shrink-0 rounded-xl p-2 text-xl text-white transition hover:bg-white/15"
                aria-label="Close navigation menu"
              >
                <FaTimes />
              </button>
            </div>

            {/* Same link spacing, icon size, and active state as Homepage */}
            <nav className="flex-1 space-y-2">
              {visibleLinks.map(({ label, path, icon: Icon }) => {
                const active = location.pathname === path;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => goTo(path)}
                    className={`box-border flex w-full min-w-0 items-center gap-3 rounded-xl px-4 py-3.5 text-left transition ${
                      active
                        ? "bg-white font-semibold text-teal-700 shadow-sm"
                        : "font-medium text-white hover:bg-white/15"
                    }`}
                  >
                    <Icon className="shrink-0 text-lg" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-5 space-y-3 border-t border-white/20 pt-5">
              {loggedUser ? (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      goTo(
                        loggedUser.role === "candidate"
                          ? "/candidate-dashboard"
                          : "/institution"
                      )
                    }
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-teal-700 transition hover:bg-teal-50"
                  >
                    <FaUserCircle /> Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      if (onLogout) {
                        onLogout();
                      } else {
                        localStorage.removeItem("user");
                        localStorage.removeItem("token");
                        navigate("/");
                      }
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 font-semibold text-white transition hover:bg-red-600"
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (onLogin) onLogin();
                    else navigate("/login");
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-teal-700 transition hover:bg-teal-50"
                >
                  <FaUser /> Login
                </button>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
